import React, { useCallback, useMemo, useState } from 'react';
import { View, StyleSheet, Pressable, Platform } from 'react-native';
import { useRouter } from 'expo-router';
import { useTheme } from '@/context/ThemeContext';
import { useModalStore } from '@/store';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import ChecklistItemRow from './ChecklistItemRow';
import ChecklistDismissButton from './ChecklistDismissButton';
import * as DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import { useChecklist, runChecklistAction, trackChecklistEvent } from '@/constants/checklist';
import useResponsiveness from '@/hooks/useResponsiveness';

// ─── Daily claim pill (standalone, post-checklist) ───────────

function DailyClaimPill() {
  const { colors } = useTheme();
  const {
    dailyLoading, dailyAvailable, dailyClaiming, dailyJustClaimed, claimDaily,
  } = useChecklist();

  const handlePress = useCallback(async () => {
    if (!dailyAvailable || dailyClaiming) return;
    trackChecklistEvent('daily_claim_pill_clicked');
    await claimDaily();
  }, [dailyAvailable, dailyClaiming, claimDaily]);

  if (!dailyLoading && !dailyAvailable && !dailyJustClaimed) return null;

  const busy = dailyLoading || dailyClaiming;

  return (
    <Pressable
      onPress={handlePress}
      disabled={busy || dailyJustClaimed}
      style={({ hovered }) => [
        styles.pill,
        {
          backgroundColor: hovered && !busy && !dailyJustClaimed
            ? colors.solid_color_tertiary : 'transparent',
          borderColor: colors.border_color_secondary,
        },
        busy && { opacity: 0.6 },
      ]}
      accessibilityRole="button"
      accessibilityLabel={dailyJustClaimed ? 'Daily credits claimed' : 'Claim daily credits'}
    >
      <View style={styles.pillRingWrap}>
        <Icon
          name={dailyJustClaimed ? 'circle-check' : 'calendar-star'}
          size={14}
          color={colors.text_color_secondary}
        />
      </View>
      <Text size="xxs" weight="semibold" color="secondary">
        {dailyJustClaimed ? 'Claimed!' : 'Daily reward'}
      </Text>
    </Pressable>
  );
}

// ─── Checklist pill with popover (pre-completion) ────────────

function ChecklistPill() {
  const { colors } = useTheme();
  const router = useRouter();
  const { openModal } = useModalStore(state => state.actions);
  const { isXS } = useResponsiveness();
  const [open, setOpen] = useState(false);
  const {
    completionMap, completedCount, sortedItems, total,
    dailyAvailable, dailyClaiming, dailyJustClaimed, claimDaily,
    showCredits, dismiss,
  } = useChecklist();

  const handleItemPress = useCallback((item) => {
    trackChecklistEvent('item_clicked', item.id);
    setOpen(false);
    if (item.action) {
      runChecklistAction(item.action, { router, openModal, isXS });
      return;
    }
    if (item.route) {
      router.push(item.route);
    }
  }, [router, openModal, isXS]);

  const handleEarnMore = useCallback(() => {
    trackChecklistEvent('earn_more_clicked');
    setOpen(false);
    openModal('SettingsModalWeb', { tab: 'earn' });
  }, [openModal]);

  const handleDailyClaim = useCallback(async () => {
    if (!dailyAvailable || dailyClaiming) return;
    trackChecklistEvent('daily_claim_popover_clicked');
    await claimDaily();
  }, [dailyAvailable, dailyClaiming, claimDaily]);

  const pct = useMemo(() => Math.round((completedCount / total) * 100), [completedCount, total]);
  const circumference = 2 * Math.PI * 8;
  const strokeDashoffset = circumference - (pct / 100) * circumference;
  const showDailyRow = showCredits && (dailyAvailable || dailyJustClaimed);

  const TriggerComponent = React.memo(() => (
    <Pressable
      style={({ hovered }) => [
        styles.pill,
        {
          backgroundColor: hovered
            ? colors.solid_color_tertiary
            : 'transparent',
          borderColor: colors.border_color_secondary,
        },
      ]}
      accessibilityRole="button"
      accessibilityLabel={`Get started checklist: ${completedCount} of ${total} complete`}
    >
      <View style={styles.pillRingWrap}>
        <svg width={20} height={20} viewBox="0 0 20 20" style={{ position: 'absolute' }}>
          <circle cx={10} cy={10} r={8} fill="none" stroke={colors.border_color_secondary} strokeWidth={2} />
          <circle
            cx={10} cy={10} r={8}
            fill="none"
            stroke={colors.text_color_secondary}
            strokeWidth={2}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            transform="rotate(-90 10 10)"
          />
        </svg>
      </View>
      <Text size="xxs" weight="semibold" color='secondary'>
        Get Started
      </Text>
      <Text size="xxs" color="tertiary" style={{ fontVariant: ['tabular-nums'] }}>
        {completedCount}/{total}
      </Text>
    </Pressable>
  ));
  TriggerComponent.displayName = 'ChecklistTrigger';

  return (
    <DropdownMenu.Root open={open} onOpenChange={setOpen}>
      <DropdownMenu.Trigger>
        <TriggerComponent />
      </DropdownMenu.Trigger>

      <DropdownMenu.Content
        className={Platform.OS === 'web' ? 'DropdownMenuContent' : undefined}
        align="end"
        sideOffset={5}
      >
        <View style={styles.popover}>
          {/* Daily claim row */}
          {showDailyRow && (
            <Pressable
              onPress={handleDailyClaim}
              accessibilityRole="button"
              accessibilityLabel={dailyJustClaimed ? 'Daily credits claimed' : 'Claim daily credits'}
              disabled={dailyClaiming || dailyJustClaimed}
              style={({ hovered }) => [
                styles.dailyRow,
                { borderBottomColor: colors.border_color_secondary },
                hovered && !dailyJustClaimed && { backgroundColor: colors.solid_color_tertiary },
                dailyClaiming && { opacity: 0.6 },
              ]}
            >
              <View style={styles.dailyIconWrap}>
                <Icon
                  name={dailyJustClaimed ? 'circle-check' : 'calendar-star'}
                  size={12}
                  color={colors.text_color_secondary}
                />
              </View>
              <View style={{ flex: 1 }}>
                <Text size="xs" weight="semibold" style={{ color: dailyJustClaimed ? colors.text_color_secondary : colors.text_color_primary }}>
                  {dailyJustClaimed ? 'Daily credits claimed!' : 'Claim daily credits'}
                </Text>
                <Text size="xxs" color='tertiary'>
                  {dailyJustClaimed ? 'Come back tomorrow' : 'Tap to collect your free credits'}
                </Text>
              </View>
            </Pressable>
          )}

          {/* Header */}
          <View style={styles.popoverHeader}>
            <Text size="s" weight="medium" style={{ flex: 1 }}>Get Started</Text>
            <Text size="xxs" color="tertiary" style={{ fontVariant: ['tabular-nums'] }}>
              {completedCount}/{total}
            </Text>
            <ChecklistDismissButton onPress={dismiss} colors={colors} />
          </View>
          <Text size="xxs" color="tertiary" style={styles.summary}>
            Discover what you can do.
          </Text>

          {/* Progress bar */}
          <View
            accessibilityRole="progressbar"
            accessibilityLabel="Getting started progress"
            accessibilityValue={{ min: 0, max: total, now: completedCount }}
            style={[styles.progressTrack, { backgroundColor: colors.border_color_secondary }]}
          >
            <View style={[
              styles.progressFill,
              {
                width: `${pct}%`,
                backgroundColor: colors.text_color_secondary,
              },
            ]} />
          </View>

          {/* Items */}
          <View style={styles.items}>
            {sortedItems.map((item) => (
              <ChecklistItemRow
                key={item.id}
                item={item}
                completed={completionMap[item.id]}
                onPress={() => handleItemPress(item)}
                colors={colors}
              />
            ))}
          </View>

          {showCredits && (
            <View style={[styles.footer, { borderTopColor: colors.border_color_secondary }]}>
              <Pressable
                onPress={handleEarnMore}
                accessibilityRole="button"
                accessibilityLabel="View all rewards"
                style={({ hovered }) => [
                  styles.footerBtn,
                  hovered && { backgroundColor: colors.solid_color_tertiary },
                ]}
              >
                <Text size="xxs" weight="semibold" style={{ color: colors.text_color_secondary, flex: 1 }}>
                  View all rewards
                </Text>
                <Icon name="chevron-right" size={10} color={colors.text_color_tertiary} />
              </Pressable>
            </View>
          )}
        </View>
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
}

// ─── Router: picks the right mode ────────────────────────────

function ChecklistProgressPill() {
  const { shouldShow, shouldShowDaily, isComplete } = useChecklist();

  if (shouldShow) return <ChecklistPill />;
  if (shouldShowDaily && isComplete) return <DailyClaimPill />;
  return null;
}

const styles = StyleSheet.create({
  pill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingLeft: 6,
    paddingRight: 10,
    paddingVertical: 4,
    borderRadius: 8,
    minHeight: 32,
    borderWidth: 1,
  },
  pillRingWrap: {
    width: 20,
    height: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  popover: {
    width: 300,
    maxWidth: '100%',
    borderRadius: 12,
    overflow: 'hidden',
  },
  popoverHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingLeft: 14,
    paddingRight: 6,
    paddingTop: 8,
    paddingBottom: 4,
    gap: 8,
  },
  summary: {
    paddingHorizontal: 14,
    paddingBottom: 12,
    lineHeight: 16,
  },
  dailyRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderBottomWidth: 1,
  },
  dailyIconWrap: {
    width: 28,
    height: 28,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  progressTrack: {
    height: 2,
    marginHorizontal: 14,
    borderRadius: 2,
    overflow: 'hidden',
    marginBottom: 4,
  },
  progressFill: {
    height: '100%',
    borderRadius: 2,
  },
  items: {
    paddingHorizontal: 6,
    paddingBottom: 4,
  },
  footer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 5,
    width: '100%',
    borderTopWidth: 1,
    paddingTop: 7,
  },
  footerBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    width: '100%',
    paddingHorizontal: 14,
    paddingVertical: 10,
    minHeight: 36,
    borderRadius: 8,
  },
});

export default React.memo(ChecklistProgressPill);
